import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks for fetching data
export const fetchEmailCampaigns = createAsyncThunk(
  'contactDetail/fetchEmailCampaigns',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/campaigns?type=email`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const fetchWhatsappCampaigns = createAsyncThunk(
  'contactDetail/fetchWhatsappCampaigns',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/campaigns?type=whatsapp`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const fetchDonations = createAsyncThunk(
  'contactDetail/fetchDonations',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/donation-pledges`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const fetchReminders = createAsyncThunk(
  'contactDetail/fetchReminders',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/reminders`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const fetchTransactions = createAsyncThunk(
  'contactDetail/fetchTransactions',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/transactions`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const fetchComments = createAsyncThunk(
  'contactDetail/fetchComments',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`contacts/${contactId}/comments`);
      return { contactId, data: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const createComment = createAsyncThunk(
  'contactDetail/createComment',
  async ({ contactId, comment_text }, { rejectWithValue }) => {
    try {
      const response = await api.post(`contacts/${contactId}/comments`, { comment_text });
      return { contactId, comment: response.data };
    } catch (error) {
      return rejectWithValue({ contactId, error: error.message });
    }
  },
);

export const deleteCommentAsync = createAsyncThunk(
  'contactDetail/deleteComment',
  async ({ contactId, commentId }, { rejectWithValue }) => {
    try {
      await api.delete(`contacts/${contactId}/comments/${commentId}`);
      return { contactId, commentId };
    } catch (error) {
      return rejectWithValue({ contactId, commentId, error: error.message });
    }
  },
);

const contactDetailSlice = createSlice({
  name: 'contactDetail',
  initialState: {
    // Cache data by contact ID
    cache: {},
    // Structure: cache[contactId] = {
    //   emailCampaigns: { data: [], loading: false, error: null, loaded: false },
    //   whatsappCampaigns: { data: [], loading: false, error: null, loaded: false },
    //   donations: { data: [], loading: false, error: null, loaded: false },
    //   reminders: { data: [], loading: false, error: null, loaded: false },
    //   transactions: { data: [], loading: false, error: null, loaded: false },
    //   comments: { data: [], loading: false, error: null, loaded: false },
    // }
  },
  reducers: {
    // Add a new comment to the cache
    addComment: (state, action) => {
      const { contactId, comment } = action.payload;
      if (state.cache[contactId]?.comments?.loaded) {
        state.cache[contactId].comments.data.push(comment);
      }
    },

    // Delete a comment from the cache
    deleteComment: (state, action) => {
      const { contactId, commentId } = action.payload;
      if (state.cache[contactId]?.comments?.loaded) {
        state.cache[contactId].comments.data = state.cache[contactId].comments.data.filter(
          (c) => c.id !== commentId,
        );
      }
    },

    // Clear cache for a specific contact (useful when contact is updated)
    clearContactCache: (state, action) => {
      const { contactId } = action.payload;
      delete state.cache[contactId];
    },

    // Clear all cache (useful on logout)
    clearAllCache: (state) => {
      state.cache = {};
    },
  },
  extraReducers: (builder) => {
    // Email Campaigns
    builder
      .addCase(fetchEmailCampaigns.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].emailCampaigns) {
          state.cache[contactId].emailCampaigns = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].emailCampaigns.loading = true;
        state.cache[contactId].emailCampaigns.error = null;
      })
      .addCase(fetchEmailCampaigns.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].emailCampaigns = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchEmailCampaigns.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].emailCampaigns) {
          state.cache[contactId].emailCampaigns = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].emailCampaigns.loading = false;
        state.cache[contactId].emailCampaigns.error = action.payload?.error || action.error.message;
        state.cache[contactId].emailCampaigns.loaded = true;
      });

    // WhatsApp Campaigns
    builder
      .addCase(fetchWhatsappCampaigns.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].whatsappCampaigns) {
          state.cache[contactId].whatsappCampaigns = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].whatsappCampaigns.loading = true;
        state.cache[contactId].whatsappCampaigns.error = null;
      })
      .addCase(fetchWhatsappCampaigns.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].whatsappCampaigns = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchWhatsappCampaigns.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].whatsappCampaigns) {
          state.cache[contactId].whatsappCampaigns = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].whatsappCampaigns.loading = false;
        state.cache[contactId].whatsappCampaigns.error =
          action.payload?.error || action.error.message;
        state.cache[contactId].whatsappCampaigns.loaded = true;
      });

    // Donations
    builder
      .addCase(fetchDonations.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].donations) {
          state.cache[contactId].donations = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].donations.loading = true;
        state.cache[contactId].donations.error = null;
      })
      .addCase(fetchDonations.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].donations = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchDonations.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].donations) {
          state.cache[contactId].donations = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].donations.loading = false;
        state.cache[contactId].donations.error = action.payload?.error || action.error.message;
        state.cache[contactId].donations.loaded = true;
      });

    // Reminders
    builder
      .addCase(fetchReminders.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].reminders) {
          state.cache[contactId].reminders = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].reminders.loading = true;
        state.cache[contactId].reminders.error = null;
      })
      .addCase(fetchReminders.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].reminders = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchReminders.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].reminders) {
          state.cache[contactId].reminders = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].reminders.loading = false;
        state.cache[contactId].reminders.error = action.payload?.error || action.error.message;
        state.cache[contactId].reminders.loaded = true;
      });

    // Comments
    builder
      .addCase(fetchComments.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].comments) {
          state.cache[contactId].comments = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].comments.loading = true;
        state.cache[contactId].comments.error = null;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].comments = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchComments.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].comments) {
          state.cache[contactId].comments = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].comments.loading = false;
        state.cache[contactId].comments.error = action.payload?.error || action.error.message;
        state.cache[contactId].comments.loaded = true;
      });

    // Create Comment
    builder
      .addCase(createComment.pending, (state, action) => {
        const { contactId } = action.meta.arg;
        if (state.cache[contactId]?.comments) {
          state.cache[contactId].comments.loading = true;
        }
      })
      .addCase(createComment.fulfilled, (state, action) => {
        const { contactId, comment } = action.payload;
        if (state.cache[contactId]?.comments?.loaded) {
          state.cache[contactId].comments.data.push(comment);
          state.cache[contactId].comments.loading = false;
        }
      })
      .addCase(createComment.rejected, (state, action) => {
        const { contactId } = action.meta.arg;
        if (state.cache[contactId]?.comments) {
          state.cache[contactId].comments.loading = false;
          state.cache[contactId].comments.error = action.payload?.error || action.error.message;
        }
      });

    // Delete Comment
    builder
      .addCase(deleteCommentAsync.pending, (state, action) => {
        const { contactId } = action.meta.arg;
        if (state.cache[contactId]?.comments) {
          state.cache[contactId].comments.loading = true;
        }
      })
      .addCase(deleteCommentAsync.fulfilled, (state, action) => {
        const { contactId, commentId } = action.payload;
        if (state.cache[contactId]?.comments?.loaded) {
          state.cache[contactId].comments.data = state.cache[contactId].comments.data.filter(
            (c) => c.id !== commentId,
          );
          state.cache[contactId].comments.loading = false;
        }
      })
      .addCase(deleteCommentAsync.rejected, (state, action) => {
        const { contactId } = action.meta.arg;
        if (state.cache[contactId]?.comments) {
          state.cache[contactId].comments.loading = false;
          state.cache[contactId].comments.error = action.payload?.error || action.error.message;
        }
      });

    // Transactions
    builder
      .addCase(fetchTransactions.pending, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].transactions) {
          state.cache[contactId].transactions = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].transactions.loading = true;
        state.cache[contactId].transactions.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        const { contactId, data } = action.payload;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        state.cache[contactId].transactions = {
          data,
          loading: false,
          error: null,
          loaded: true,
        };
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        const contactId = action.meta.arg;
        if (!state.cache[contactId]) {
          state.cache[contactId] = {};
        }
        if (!state.cache[contactId].transactions) {
          state.cache[contactId].transactions = {
            data: [],
            loading: false,
            error: null,
            loaded: false,
          };
        }
        state.cache[contactId].transactions.loading = false;
        state.cache[contactId].transactions.error = action.payload?.error || action.error.message;
        state.cache[contactId].transactions.loaded = true;
      });
  },
});

export const { addComment, deleteComment, clearContactCache, clearAllCache } =
  contactDetailSlice.actions;

// Selectors
export const selectContactCache = (state, contactId) => state.contactDetail.cache[contactId] || {};

export const selectEmailCampaigns = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.emailCampaigns || { data: [], loading: false, error: null, loaded: false };
};

export const selectWhatsappCampaigns = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.whatsappCampaigns || { data: [], loading: false, error: null, loaded: false };
};

export const selectDonations = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.donations || { data: [], loading: false, error: null, loaded: false };
};

export const selectReminders = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.reminders || { data: [], loading: false, error: null, loaded: false };
};

export const selectComments = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.comments || { data: [], loading: false, error: null, loaded: false };
};

export const selectTransactions = (state, contactId) => {
  const cache = state.contactDetail.cache[contactId];
  return cache?.transactions || { data: [], loading: false, error: null, loaded: false };
};

export default contactDetailSlice.reducer;
